import { differenceInDays, format } from 'date-fns';
import { 
  Box, 
  TextField, 
  MenuItem, 
  Alert,
} from '@mui/material';
import { formatDate } from 'state/helpers';
import CustomDatePicker from 'components/CustomDatePicker';
import SectionLabel from 'components/SectionLabel';
import FieldDisplay from 'components/FieldDisplay';


const ExpiryAlert = ({ label, date }) => {
  if (!date) return null;
  const days = differenceInDays(new Date(date), new Date());
  if (days >= 180) return null;
  return (
    <Box sx={{ gridColumn: 'span 12' }}>
      <Alert severity={days < 0 ? 'error' : 'warning'} sx={{ py: 0 }}>
        {days < 0
          ? `${label} expired ${Math.abs(days)} day(s) ago.`
          : `${label} expires in ${days} day(s) (${format(new Date(date), 'dd/MM/yyyy')}).`}
      </Alert>
    </Box>
  );
};

const PASSPORT_TYPES = [
  { value: 'P', label: 'Personal' },
  { value: 'D', label: 'Diplomatic' },
  { value: 'F', label: 'Official' },
];


const CrewDocuments = ({ values, handleChange, handleBlur, setFieldValue, isEditMode }) => {
  if (!isEditMode) return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <SectionLabel>Licence</SectionLabel>
        <Box 
          display="grid" 
          gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
          gap={2} 
        >
          <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <FieldDisplay label="Licence Number" value={values.licenceNum} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}>
            <FieldDisplay label="Issue Date" value={formatDate(values.licenceIssueDate)} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}>
            <FieldDisplay label="Expiry Date" value={formatDate(values.licenceExpiryDate)} />
          </Box>
          <ExpiryAlert label="Licence" date={values.licenceExpiryDate} />
        </Box>
      </Box>

      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <SectionLabel>Passport</SectionLabel>
        <Box 
          display="grid" 
          gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
          gap={2} 
        >
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="Type" value={values.passportType} /></Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="Number" value={values.passportNumber} /></Box>
          <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}><FieldDisplay label="Passport Name" value={values.passportName} /></Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="First Name" value={values.firstName} /></Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="Middle Name" value={values.middleName} /></Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="Surname" value={values.surname} /></Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="Issue Date" value={formatDate(values.passportIssueDate)} /></Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="Expiry Date" value={formatDate(values.passportExpiryDate)} /></Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="Country" value={values.passportCountry} /></Box>
          <ExpiryAlert label="Passport" date={values.passportExpiryDate} />
        </Box>
      </Box>

      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <SectionLabel>National ID</SectionLabel>
        <Box 
          display="grid" 
          gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
          gap={2} 
        >
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="ID Number" value={values.nationalIdNumber} /></Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="Issue Date" value={formatDate(values.nationalIdIssueDate)} /></Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}><FieldDisplay label="Issue Place" value={values.nationalIdIssuePlace} /></Box>
        </Box>
      </Box>

      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <SectionLabel>Tax</SectionLabel>
        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 12' } }}><FieldDisplay label="Tax Code" value={values.taxCode} /></Box>
      </Box>
    </Box>
  );

  return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <SectionLabel>Licence</SectionLabel>
      <TextField
        fullWidth
        size="small"
        label="Licence Number"
        name="licenceNum"
        value={values.licenceNum}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <CustomDatePicker
        variant="outlined"
        size="small"
        label="Issue Date"
        name="licenceIssueDate"
        value={values.licenceIssueDate ? new Date(values.licenceIssueDate) : null}
        maxDate={values.licenceExpiryDate ? new Date(values.licenceExpiryDate) : null}
        onChange={setFieldValue}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <CustomDatePicker
        variant="outlined"
        size="small"
        label="Expiry Date"
        name="licenceExpiryDate"
        value={values.licenceExpiryDate ? new Date(values.licenceExpiryDate) : null}
        minDate={values.licenceIssueDate ? new Date(values.licenceIssueDate) : null}
        onChange={setFieldValue}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />

      <SectionLabel>Passport</SectionLabel>
      <TextField 
        fullWidth 
        select 
        size="small" 
        label="Type" 
        name="passportType"
        value={values.passportType || ''} 
        onChange={handleChange} 
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      >
        {PASSPORT_TYPES.map(p => (
          <MenuItem key={p.value} value={p.value}>
            {p.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField 
        fullWidth 
        size="small" 
        label="Passport Number" 
        name="passportNumber"
        value={values.passportNumber} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Passport Name" 
        name="passportName"
        value={values.passportName} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="First Name" 
        name="firstName"
        value={values.firstName} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Middle Name" 
        name="middleName"
        value={values.middleName} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Surname" 
        name="surname"
        value={values.surname} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />      
      <CustomDatePicker 
        variant="outlined"
        size="small" 
        label="Issue Date" 
        name="passportIssueDate"
        value={values.passportIssueDate ? new Date(values.passportIssueDate) : null}
        maxDate={values.passportExpiryDate ? new Date(values.passportExpiryDate) : null}
        onChange={setFieldValue} 
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <CustomDatePicker 
        variant="outlined"
        size="small" 
        label="Expiry Date" 
        name="passportExpiryDate"
        value={values.passportExpiryDate ? new Date(values.passportExpiryDate) : null}
        minDate={values.passportIssueDate ? new Date(values.passportIssueDate) : null}
        onChange={setFieldValue} 
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Country" 
        name="passportCountry"
        value={values.passportCountry} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      
      <SectionLabel>National ID</SectionLabel>
      <TextField 
        fullWidth 
        size="small" 
        label="ID Number" 
        name="nationalIdNumber"
        value={values.nationalIdNumber} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <CustomDatePicker 
        variant="outlined"
        size="small" 
        label="Issue Date" 
        name="nationalIdIssueDate"
        value={values.nationalIdIssueDate ? new Date(values.nationalIdIssueDate) : null}
        onChange={setFieldValue} 
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Issue Place" 
        name="nationalIdIssuePlace"
        value={values.nationalIdIssuePlace} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />

      <SectionLabel>Tax</SectionLabel>
      <TextField 
        fullWidth 
        size="small" 
        label="Tax Code" 
        name="taxCode"
        value={values.taxCode} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 12' } }}
      />
    </Box>
  );
};

export default CrewDocuments;
